/**
 * Data Cleanup Panel
 *
 * Admin/utility component for running data cleanup scripts
 * Helps identify and fix orphaned data
 */

import React, { useState } from 'react';
import { AlertTriangle, RefreshCw, Trash2, CheckCircle, XCircle, Info } from 'lucide-react';
import { useAuth } from '../AuthContext';
import {
  findOrphanedInvestments,
  deleteOrphanedInvestments,
  checkDataIntegrity,
  formatCleanupReport
} from '../utils/dataCleanup';

const DataCleanupPanel = ({ isDark }) => {
  const { currentUser } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  const handleScan = async () => {
    if (!currentUser) {
      alert('Please log in first');
      return;
    }

    setScanning(true);
    setError(null);
    setReport(null);

    try {
      const integrityReport = await checkDataIntegrity(currentUser.uid);
      setReport(integrityReport);

      if (integrityReport.healthy) {
        alert('✅ Data integrity check passed!\n\nNo orphaned data found.');
      } else {
        alert(`⚠️ Found ${integrityReport.issueCount} data integrity issue(s).\n\nCheck the report below for details.`);
      }
    } catch (err) {
      console.error('Scan error:', err);
      setError(err.message);
      alert('Error scanning data: ' + err.message);
    } finally {
      setScanning(false);
    }
  };

  const handleCleanup = async (dryRun = true) => {
    if (!currentUser) {
      alert('Please log in first');
      return;
    }

    if (!dryRun) {
      const confirmed = window.confirm(
        '⚠️ WARNING: This will permanently delete orphaned investments!\n\n' +
        'This action cannot be undone.\n\n' +
        'Are you absolutely sure you want to proceed?'
      );

      if (!confirmed) return;
    }

    setCleaning(true);
    setError(null);

    try {
      const cleanupReport = await deleteOrphanedInvestments(currentUser.uid, dryRun);
      setReport(cleanupReport);

      if (dryRun) {
        alert(
          `DRY RUN COMPLETE\n\n` +
          `Would delete: ${cleanupReport.orphanedCount} orphaned investment(s)\n` +
          `Total value: $${cleanupReport.orphanedValue.toFixed(2)}\n\n` +
          `Click "Clean Up Orphaned Data" to actually delete.`
        );
      } else {
        alert(
          `CLEANUP COMPLETE\n\n` +
          `Deleted: ${cleanupReport.deleted} orphaned investment(s)\n` +
          `Total value: $${cleanupReport.orphanedValue.toFixed(2)}`
        );
        // Refresh the page to show updated data
        setTimeout(() => window.location.reload(), 1000);
      }
    } catch (err) {
      console.error('Cleanup error:', err);
      setError(err.message);
      alert('Error during cleanup: ' + err.message);
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className={`space-y-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
      {/* Header */}
      <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg border-l-4 border-yellow-500`}>
        <div className="flex items-start gap-4">
          <AlertTriangle className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
          <div>
            <h2 className="text-2xl font-bold mb-2">Data Cleanup Utility</h2>
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              This tool helps identify and fix data consistency issues in your portfolio,
              such as orphaned investments that belong to deleted accounts.
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
        <h3 className="text-lg font-semibold mb-4">Actions</h3>

        <div className="space-y-3">
          {/* Scan Button */}
          <button
            onClick={handleScan}
            disabled={scanning || cleaning}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {scanning ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Scanning...
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5" />
                Scan for Data Issues
              </>
            )}
          </button>

          {/* Preview Cleanup Button */}
          <button
            onClick={() => handleCleanup(true)}
            disabled={scanning || cleaning}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cleaning ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Info className="w-5 h-5" />
                Preview Cleanup (Dry Run)
              </>
            )}
          </button>

          {/* Actual Cleanup Button */}
          {report && report.orphanedCount > 0 && (
            <button
              onClick={() => handleCleanup(false)}
              disabled={scanning || cleaning}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cleaning ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Cleaning...
                </>
              ) : (
                <>
                  <Trash2 className="w-5 h-5" />
                  Clean Up Orphaned Data (Permanent!)
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className={`p-4 rounded-lg bg-red-500 bg-opacity-10 border border-red-500`}>
          <div className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-500 font-semibold">Error: {error}</p>
          </div>
        </div>
      )}

      {/* Report Display */}
      {report && (
        <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex items-center gap-2 mb-4">
            {report.healthy ? (
              <CheckCircle className="w-6 h-6 text-green-500" />
            ) : (
              <AlertTriangle className="w-6 h-6 text-yellow-500" />
            )}
            <h3 className="text-lg font-semibold">
              {report.healthy ? 'Data Integrity: Healthy' : 'Data Integrity Issues Found'}
            </h3>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Total Investments
              </p>
              <p className="text-2xl font-bold">{report.totalInvestments || 0}</p>
            </div>

            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Total Accounts
              </p>
              <p className="text-2xl font-bold">{report.totalAccounts || 0}</p>
            </div>

            <div className={`p-4 rounded-lg ${
              report.orphanedCount > 0
                ? 'bg-yellow-500 bg-opacity-20 border border-yellow-500'
                : isDark ? 'bg-gray-700' : 'bg-gray-100'
            }`}>
              <p className={`text-sm ${
                report.orphanedCount > 0
                  ? 'text-yellow-600 dark:text-yellow-400'
                  : isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Orphaned Investments
              </p>
              <p className={`text-2xl font-bold ${
                report.orphanedCount > 0 ? 'text-yellow-600 dark:text-yellow-400' : ''
              }`}>
                {report.orphanedCount || 0}
              </p>
            </div>
          </div>

          {/* Orphaned Investments List */}
          {report.orphanedInvestments && report.orphanedInvestments.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3">Orphaned Investments Found:</h4>
              <div className="space-y-2">
                {report.orphanedInvestments.map((inv, index) => (
                  <div
                    key={inv.id}
                    className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold">{inv.name || 'Unknown'}</p>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Ticker: {inv.ticker || 'N/A'} | Category: {inv.category || 'Unknown'}
                        </p>
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mt-1`}>
                          Missing Account ID: {inv.accountId}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">
                          ${(inv.amount || 0).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className={`mt-4 p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <p className="font-semibold">
                  Total Orphaned Value: ${(report.orphanedValue || 0).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </p>
              </div>
            </div>
          )}

          {/* Dry Run Notice */}
          {report.dryRun !== undefined && (
            <div className={`mt-4 p-3 rounded-lg ${
              report.dryRun
                ? 'bg-blue-500 bg-opacity-10 border border-blue-500'
                : 'bg-green-500 bg-opacity-10 border border-green-500'
            }`}>
              <p className={`text-sm ${
                report.dryRun ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'
              }`}>
                {report.dryRun
                  ? '🔍 This was a dry run - no data was deleted'
                  : `✅ Cleanup complete - ${report.deleted} investment(s) deleted`
                }
              </p>
            </div>
          )}
        </div>
      )}

      {/* Info Box */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'} border border-blue-500`}>
        <h4 className="font-semibold mb-2 text-blue-600 dark:text-blue-400">How to Use This Tool:</h4>
        <ol className={`list-decimal list-inside space-y-1 text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
          <li>Click "Scan for Data Issues" to check your data</li>
          <li>If issues are found, click "Preview Cleanup" to see what would be deleted</li>
          <li>Review the orphaned investments carefully</li>
          <li>Click "Clean Up Orphaned Data" to permanently delete orphaned records</li>
        </ol>
      </div>
    </div>
  );
};

export default DataCleanupPanel;
